"use client";

import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Field,
  HStack,
  Input,
  Stack,
  Text,
  Textarea,
  VisuallyHidden,
} from "@chakra-ui/react";
import type {
  GetPublicFeedbackFormResult,
  TranscribeAudioMetadata,
} from "@/generated/fastapi";
import { AudioTranscriptInputFlow } from "@/features/audio-input";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import {
  PublicActionButton,
  PublicCard,
  PublicMessage,
} from "@/shared/public-ui";
import useSubmitPublicFeedbackMutation from "../mutations/useSubmitPublicFeedbackMutation";
import useTranscribePublicFeedbackMutation from "../mutations/useTranscribePublicFeedbackMutation";

const MIN_FEEDBACK_LENGTH = 3;

const MODE_BUTTON_FOCUS_PROPS = {
  outlineWidth: "2",
  outlineStyle: "solid",
  outlineColor: "moveon.primary",
  outlineOffset: "2px",
} as const;

type FeedbackInputMode = "text" | "voice";

type PublicFeedbackFormProps = {
  token: string;
  form: GetPublicFeedbackFormResult;
};

function normalizeFeedbackText(value: string) {
  return value.replace(/\s+/g, " ").trim();
}

function getInitialMode(form: GetPublicFeedbackFormResult): FeedbackInputMode {
  if (form.allow_text === false && form.allow_voice) {
    return "voice";
  }

  return "text";
}

export default function PublicFeedbackForm({
  token,
  form,
}: PublicFeedbackFormProps) {
  const [feedbackText, setFeedbackText] = useState("");
  const [contactWebsite, setContactWebsite] = useState("");
  const [inputMode, setInputMode] = useState<FeedbackInputMode>(() =>
    getInitialMode(form),
  );
  const [transcriptionMeta, setTranscriptionMeta] =
    useState<TranscribeAudioMetadata | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const submitFeedbackMutation = useSubmitPublicFeedbackMutation(token);
  const transcribeFeedbackMutation = useTranscribePublicFeedbackMutation(token);

  const allowText = form.allow_text !== false;
  const allowVoice = form.allow_voice === true;
  const normalizedFeedbackText = useMemo(
    () => normalizeFeedbackText(feedbackText),
    [feedbackText],
  );
  const canSubmit =
    normalizedFeedbackText.length >= MIN_FEEDBACK_LENGTH &&
    !submitFeedbackMutation.isPending;

  const handleTranscribe = async (selectedFile: File) => {
    setErrorMessage(null);
    try {
      const result = await transcribeFeedbackMutation.mutateAsync(selectedFile);
      setFeedbackText(result.transcribed_text ?? "");
      setTranscriptionMeta(result.transcription_meta ?? null);
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Failed to transcribe feedback.",
      );
    }
  };

  const handleResetTranscript = () => {
    setFeedbackText("");
    setTranscriptionMeta(null);
    setErrorMessage(null);
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }

    setErrorMessage(null);
    try {
      await submitFeedbackMutation.mutateAsync({
        feedback_text: normalizedFeedbackText,
        contact_website: contactWebsite,
      });
      setIsSubmitted(true);
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "Failed to send feedback.",
      );
    }
  };

  const handleProvideAgain = () => {
    setFeedbackText("");
    setContactWebsite("");
    setTranscriptionMeta(null);
    setErrorMessage(null);
    setIsSubmitted(false);
    setInputMode(getInitialMode(form));
  };

  if (isSubmitted) {
    return (
      <PublicCard
        as="section"
        tone="success"
        variant="tinted"
        maxW="container.sm"
        mx="auto"
        py={{ base: "8", md: "10" }}
        textAlign="center"
      >
        <Stack gap="4" align="center">
          <Text
            as="h2"
            fontSize={{ base: "lg", md: "xl" }}
            fontWeight="bold"
            color="moveon.success"
          >
            Feedback sent
          </Text>
          <Text color="moveon.publicFont">
            Thank you. Your feedback was sent privately.
          </Text>
          <Button variant="outline" onClick={handleProvideAgain}>
            <GenericIcon icon="replay" humanReadable="Provide again" />
            Provide again
          </Button>
        </Stack>
      </PublicCard>
    );
  }

  if (!allowText && !allowVoice) {
    return (
      <PublicMessage
        title="Feedback unavailable"
        message="This feedback link is not accepting responses right now."
        type="warning"
      />
    );
  }

  return (
    <PublicCard as="section" variant="task" tone="primary">
      <Stack gap="5" align="stretch">
        <Stack gap="2" align="stretch">
          {form.request_title ? (
            <Text as="h2" fontSize={{ base: "lg", md: "xl" }} fontWeight="bold">
              {form.request_title}
            </Text>
          ) : null}
          {form.request_message ? (
            <Text color="moveon.publicFont" lineHeight="1.7">
              {form.request_message}
            </Text>
          ) : null}
        </Stack>

        {allowVoice ? (
          <HStack gap="2" flexWrap="wrap">
            {allowText ? (
              <Button
                aria-pressed={inputMode === "text"}
                variant={inputMode === "text" ? "solid" : "outline"}
                bg={inputMode === "text" ? "moveon.primary" : undefined}
                color={
                  inputMode === "text"
                    ? "moveon.public.surface.base"
                    : "moveon.publicFont"
                }
                borderColor="moveon.public.surface.border"
                _hover={{
                  bg:
                    inputMode === "text"
                      ? "moveon.primary"
                      : "moveon.public.surface.accent",
                }}
                _focusVisible={MODE_BUTTON_FOCUS_PROPS}
                onClick={() => setInputMode("text")}
              >
                <GenericIcon icon="edit" humanReadable="Write" />
                Write
              </Button>
            ) : null}
            <Button
              aria-pressed={inputMode === "voice"}
              variant={inputMode === "voice" ? "solid" : "outline"}
              bg={inputMode === "voice" ? "moveon.primary" : undefined}
              color={
                inputMode === "voice"
                  ? "moveon.public.surface.base"
                  : "moveon.publicFont"
              }
              borderColor="moveon.public.surface.border"
              _hover={{
                bg:
                  inputMode === "voice"
                    ? "moveon.primary"
                    : "moveon.public.surface.accent",
              }}
              _focusVisible={MODE_BUTTON_FOCUS_PROPS}
              onClick={() => setInputMode("voice")}
            >
              <GenericIcon icon="record" humanReadable="Voice" />
              Record
            </Button>
          </HStack>
        ) : null}

        {inputMode === "voice" && allowVoice ? (
          <AudioTranscriptInputFlow
            errorMessage={errorMessage}
            isError={Boolean(errorMessage)}
            isTranscribing={transcribeFeedbackMutation.isPending}
            mode="voice"
            readyMessage="Transcript received. You can edit it below before sending."
            transcriptValue={feedbackText}
            transcriptionMeta={transcriptionMeta}
            onReset={handleResetTranscript}
            onTranscribe={handleTranscribe}
          />
        ) : null}

        {(inputMode === "text" && allowText) || feedbackText ? (
          <Field.Root>
            <Field.Label>Feedback</Field.Label>
            <Textarea
              value={feedbackText}
              onChange={(event) => setFeedbackText(event.target.value)}
              placeholder="Share what this person does well, where you saw it, and what made it useful."
              minH={{ base: "44", md: "52" }}
            />
            <Field.HelperText>
              Write in your own words. A few specific examples are more useful
              than a polished recommendation.
            </Field.HelperText>
          </Field.Root>
        ) : null}

        <VisuallyHidden>
          <Field.Root>
            <Field.Label>Website</Field.Label>
            <Input
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={contactWebsite}
              onChange={(event) => setContactWebsite(event.target.value)}
            />
          </Field.Root>
        </VisuallyHidden>

        {errorMessage ? (
          <Alert.Root status="error" borderRadius="md">
            <Alert.Indicator />
            <Alert.Description>{errorMessage}</Alert.Description>
          </Alert.Root>
        ) : null}

        <Box>
          <PublicActionButton
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={submitFeedbackMutation.isPending}
            loadingText="Sending"
          >
            <GenericIcon icon="send" humanReadable="Send feedback" />
            Send feedback
          </PublicActionButton>
        </Box>
      </Stack>
    </PublicCard>
  );
}
